import dayjs from "dayjs";

import { QueueTaskStatus } from "@/hooks/core/useAsyncTaskQueue";
import { OPFSStorage } from "@/Services/OPFSStorage";
import { RequestQueueItem } from "@/types/requestQueue";
import { checkOPFSWriteSupport } from "@/utils";

const DIR_NAME = "request-queue";
const RETENTION_DAYS = 7;
const SEPARATOR = "&";

class OPFSQueueStorage extends OPFSStorage {
    private writePromise: Promise<void> = Promise.resolve();
    private pendingWrite: { key: string; items: RequestQueueItem[] } | null =
        null;

    private async dir() {
        return super.getDir(DIR_NAME);
    }

    private sanitize(s: string): string {
        return s.replace(/[^a-zA-Z0-9-]/g, "-");
    }

    private getDayKey(date = dayjs()): string {
        return date.format("YYYY-MM-DD");
    }

    private getFileKey(userId: string, role: string, date = dayjs()): string {
        return [
            this.sanitize(userId),
            this.sanitize(role),
            this.getDayKey(date),
        ].join(SEPARATOR);
    }

    private parseFileKey(fileName: string): {
        userId: string;
        role: string;
        date: string;
    } {
        const parts = fileName.replace(".json", "").split(SEPARATOR);

        const [userId, role, date] = parts;

        if (!userId || !role || !date) {
            throw new Error(`Invalid file name: ${fileName}`);
        }

        return { userId, role, date };
    }

    private scheduleWrite(key: string, items: RequestQueueItem[]): void {
        this.pendingWrite = { key, items };

        this.writePromise = this.writePromise
            .then(async () => {
                if (this.pendingWrite === null) return;
                const { key, items } = this.pendingWrite;
                this.pendingWrite = null;

                const dir = await this.dir();
                await this.writeJson(dir, `${key}.json`, items);
            })
            .catch((e) => console.warn("[OPFSQueueStorage] write failed", e));
    }

    async save(
        userId: string,
        role: string,
        items: RequestQueueItem[],
    ): Promise<void> {
        const key = this.getFileKey(userId, role);

        if (items.length === 0) {
            const dir = await this.dir();
            await this.deleteFile(dir, `${key}.json`);
            return;
        }

        this.scheduleWrite(key, items);
    }

    async loadPendingFromPastDays(
        userId: string,
        role: string,
        days = RETENTION_DAYS,
    ): Promise<{ date: string; items: RequestQueueItem[] }[]> {
        try {
            const dir = await this.dir();
            const cutoff = this.getDayKey(dayjs().subtract(days, "day"));
            const results: { date: string; items: RequestQueueItem[] }[] = [];
            const toDelete: string[] = [];

            for await (const [name] of dir.entries()) {
                let parsed: ReturnType<typeof this.parseFileKey>;

                try {
                    parsed = this.parseFileKey(name);
                } catch {
                    continue;
                }

                if (parsed.date < cutoff) {
                    toDelete.push(name);
                    continue;
                }

                if (
                    parsed.userId !== this.sanitize(userId) ||
                    parsed.role !== this.sanitize(role)
                ) {
                    continue;
                }

                try {
                    const items = await this.readJson<RequestQueueItem[]>(
                        dir,
                        name,
                    );
                    if (!items) continue;

                    const pending = items
                        .filter(
                            (item) => item.status !== QueueTaskStatus.Completed,
                        )
                        .map((item) => ({
                            ...item,
                            status: QueueTaskStatus.Idle,
                        }));

                    if (pending.length > 0) {
                        results.push({ date: parsed.date, items: pending });
                        toDelete.push(name);
                    }
                } catch {
                    continue;
                }
            }

            await Promise.all(
                toDelete.map((name) => dir.removeEntry(name).catch(() => null)),
            );

            return results.sort((a, b) => a.date.localeCompare(b.date));
        } catch (error) {
            console.warn(
                "[OPFSQueueStorage] loadPendingFromPastDays failed",
                error,
            );
            return [];
        }
    }

    async cleanup(retentionDays = RETENTION_DAYS): Promise<void> {
        try {
            const dir = await this.dir();
            const cutoff = this.getDayKey(
                dayjs().subtract(retentionDays, "day"),
            );

            for await (const [name] of dir.entries()) {
                let parsed: ReturnType<typeof this.parseFileKey>;

                try {
                    parsed = this.parseFileKey(name);
                } catch {
                    continue;
                }

                if (parsed.date < cutoff) {
                    await dir.removeEntry(name).catch(() => null);
                }
            }
        } catch (e) {
            console.warn("[OPFSQueueStorage] cleanup failed", e);
        }
    }
}

class NoopQueueStorage {
    async save(): Promise<void> {}
    async loadPendingFromPastDays(): Promise<[]> {
        return [];
    }
    async cleanup(): Promise<void> {}
}

const createOpfsQueueStorage = async (): Promise<
    OPFSQueueStorage | NoopQueueStorage
> => {
    const supported = await checkOPFSWriteSupport();

    console.log("[OPFSQueueStorage]", supported);

    return supported ? new OPFSQueueStorage() : new NoopQueueStorage();
};

let _opfsQueueStorage: OPFSQueueStorage | NoopQueueStorage | null = null;
const _ready = createOpfsQueueStorage().then((s) => {
    _opfsQueueStorage = s;
});

export const opfsQueueStorage = new Proxy(
    {} as OPFSQueueStorage | NoopQueueStorage,
    {
        get(_target, prop: keyof OPFSQueueStorage | keyof NoopQueueStorage) {
            return (...args: unknown[]) =>
                _ready.then(() => {
                    const method = (
                        _opfsQueueStorage as OPFSQueueStorage | NoopQueueStorage
                    )[prop];
                    if (typeof method === "function") {
                        return (method as (...a: unknown[]) => unknown).apply(
                            _opfsQueueStorage,
                            args,
                        );
                    }
                    return method;
                });
        },
    },
);
